"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Plus,
  Search,
  Mail,
  AlertCircle,
  Save,
  Loader2,
  BarChart3,
  Play,
} from "lucide-react";
import { PageLoader } from "@/components/ui/page-loader";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useEmailAgents, type EmailAgent } from "@/hooks/agents/email";
import { useAuth } from "@/hooks/auth/use-auth";
import { EmailAgentCard } from "./email-agent-card";
import { AgentsGrid } from "@/components/agents/shared/agents-grid";
import { GeneralSettings } from "./tabs/general-settings";
import { AutoReplySettings } from "./tabs/auto-reply-settings";
import { CrmSettings } from "./tabs/crm-settings";
import { DangerZoneSettings } from "./tabs/danger-zone-settings";
import { InboxSettings } from "./tabs/inbox-settings";
import { AnalyticsSettings } from "./tabs/analytics-settings";
import { KnowledgeBaseSettings } from "./tabs/knowledge-base-settings";
import { AgentStatusBadge } from "./agent-status-badge";
import { usePermissions } from "@/hooks/use-permissions";
import { EMAIL_AGENTS_PERMISSIONS, EMAIL_PERMISSIONS } from "@/lib/auth/permissions.constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AgentDeliverySettings } from "@/components/deliveries/agent-delivery-settings";
import { useAgentIntegrationStatusesQuery } from "@/hooks/queries/agent-integration-statuses";
import { TestChatDialog } from "./dialogs/test-chat-dialog";
import { AgentCreationWizard } from "@/components/agents/creation/agent-creation-wizard"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





type SettingsTab = "inbox" | "general" | "auto-reply" | "kb" | "crm" | "delivery" | "analytics" | "danger";

export function EmailAgentsPage() {


  const tUi = useTranslations("Email.AgentsEmailEmailAgentsPage")
  const tCommonUi = useTranslations("Common")

  const router = useRouter();
  const searchParams = useSearchParams();
  const editorAgentParam = searchParams.get("agentId");
  const editorSection = searchParams.get("section");

  const { agents, isLoading, error, refreshAgents } = useEmailAgents();
  const { fetchWithAuth, isAuthenticated } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [testChatAgent, setTestChatAgent] = useState<EmailAgent | null>(null);
  const [feedbackCounts, setFeedbackCounts] = useState<Record<string, number>>({});
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const pageTopRef = useRef<HTMLDivElement>(null);

  const { can } = usePermissions();

  // Use permissions instead of role checks
  const canViewAgents = can(EMAIL_AGENTS_PERMISSIONS.VIEW);
  const canManageAgents = can(EMAIL_AGENTS_PERMISSIONS.MANAGE);
  const canViewAnalytics = can(EMAIL_AGENTS_PERMISSIONS.ANALYTICS);
  const canViewEmails = can(EMAIL_PERMISSIONS.VIEW);

  const isCreatingAgent = editorAgentParam === "new";
  const editorAgentId = editorAgentParam && !isCreatingAgent ? editorAgentParam : null;
  const editorAgent = editorAgentId
    ? agents.find((agent) => agent.id === editorAgentId)
    : null;

  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    if (isCreatingAgent) return "general";
    if (editorSection === "inbox") return "inbox";
    if (editorSection === "auto-reply") return "auto-reply";
    if (editorSection === "crm") return "crm";
    if (editorSection === "delivery") return "delivery";
    if (editorSection === "danger") return "danger";
    return "inbox";
  });

  const filteredAgents = useMemo(
    () =>
      agents.filter(
        (agent) =>
          agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.inboxes?.some((inbox) =>
            inbox.address?.toLowerCase().includes(searchTerm.toLowerCase())
          ),
      ),
    [agents, searchTerm],
  );
  const agentIds = useMemo(() => agents.map(agent => agent.id), [agents]);
  const integrationStatusesQuery = useAgentIntegrationStatusesQuery(
    "email_agent",
    agentIds,
    !editorAgentParam,
  );

  const handleCreateAgent = () => {
    router.push("?page=email-agents&agentId=new");
  };

  const handleEditAgent = (agent: EmailAgent) => {
    router.push(`?page=email-agents&agentId=${agent.id}`);
  };

  const handleBackToList = () => {
    router.push("?page=email-agents");
  };

  const handleAgentSaved = (savedAgent: EmailAgent) => {
    refreshAgents();

    if (editorAgentParam === "new") {
      router.push(`?page=email-agents&agentId=${savedAgent.id}`);
      setStatusMessage({
        type: "success",
        message: tUi("agentCreatedSuccessfully"),
      });
    } else {
      setStatusMessage({
        type: "success",
        message: tUi("agentUpdatedSuccessfully"),
      });
    }

    setTimeout(() => {
      pageTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  useEffect(() => {
    if (!editorAgentParam) {
      return;
    }

    if (isCreatingAgent) {
      setActiveTab("general");
      return;
    }

    if (editorSection === "inbox") {
      setActiveTab("inbox");
    } else if (editorSection === "general") {
      setActiveTab("general");
    } else if (editorSection === "auto-reply") {
      setActiveTab("auto-reply");
    } else if (editorSection === "kb") {
      setActiveTab("kb");
    } else if (editorSection === "crm") {
      setActiveTab("crm");
    } else if (editorSection === "analytics") {
      setActiveTab("analytics");
    } else if (editorSection === "danger") {
      setActiveTab("danger");
    } else {
      setActiveTab("general");
    }
  }, [editorSection, editorAgentParam, isCreatingAgent]);

  // Load feedback counts for all agents
  const loadFeedbackCounts = useCallback(async () => {
    if (!isAuthenticated || agents.length === 0) return;

    const counts: Record<string, number> = {};

    // Load feedback counts in parallel for all agents
    await Promise.all(
      agents.map(async (agent) => {
        try {
          const [pendingRes, inProgressRes] = await Promise.all([
            fetchWithAuth(
              `/api/ai/feedback?agent_type=email_agent&status=pending&agent_id=${agent.id}&limit=1`
            ),
            fetchWithAuth(
              `/api/ai/feedback?agent_type=email_agent&status=in_progress&agent_id=${agent.id}&limit=1`
            ),
          ]);

          const pendingData = await pendingRes.json();
          const inProgressData = await inProgressRes.json();

          const total = (pendingData.total || 0) + (inProgressData.total || 0);
          if (total > 0) {
            counts[agent.id] = total;
          }
        } catch (err) {
          console.error(`Failed to load feedback count for agent ${agent.id}:`, err);
        }
      })
    );

    setFeedbackCounts(counts);
  }, [fetchWithAuth, isAuthenticated, agents]);

  useEffect(() => {
    loadFeedbackCounts();
  }, [loadFeedbackCounts]);

  // Editor view
  if (editorAgentParam) {
    // Check manage permission for settings access
    if (!canManageAgents) {
      return (
        <TooltipProvider>
          <div className="p-6 max-w-5xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></CardTitle>
              </CardHeader>
              <CardContent>
                <Alert>
                  <AlertDescription>
                    <span key={tUi("youDonTHavePermissionToManageEmail")}>{tUi("youDonTHavePermissionToManageEmail")}</span></AlertDescription>
                </Alert>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={handleBackToList}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span><span key={tUi("backToAgents")}>{tUi("backToAgents")}</span></span>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TooltipProvider>
      );
    }

    if (isLoading) {
      return (
        <TooltipProvider>
          <div className="p-6 max-w-5xl mx-auto">
            <PageLoader message={tUi("loadingAgent")} />
          </div>
        </TooltipProvider>
      );
    }

    if (!isCreatingAgent && !editorAgent) {
      return (
        <TooltipProvider>
          <div className="p-6 space-y-6 max-w-4xl mx-auto">
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium"><span key={tUi("agentNotFound")}>{tUi("agentNotFound")}</span></h3>
              <p className="text-muted-foreground mt-2">
                <span key={tUi("theAgentYouReLookingForDoesnT")}>{tUi("theAgentYouReLookingForDoesnT")}</span></p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleBackToList}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span><span key={tUi("backToAgents")}>{tUi("backToAgents")}</span></span>
              </Button>
            </div>
          </div>
        </TooltipProvider>
      );
    }

    if (isCreatingAgent) {
      return <AgentCreationWizard type="email" onCancel={handleBackToList} onCreated={id => { void refreshAgents(); router.push(`?page=email-agents&agentId=${id}`) }} />
    }

    return (
      <TooltipProvider>
        <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
        <div ref={pageTopRef} className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-2 sm:gap-3">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={handleBackToList} aria-label={tUi("backToEmailAgents")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                <h1 key={isCreatingAgent
                    ? tUi("createEmailAgent")
                    : tUi("editValue0", { value0: String(editorAgent?.name) })}
                  className="min-w-0 break-words text-xl font-semibold text-foreground sm:text-2xl"
                  title={isCreatingAgent ? tUi("createEmailAgent") : tUi("editValue0", { value0: String(editorAgent?.name) })}
                >
                  {isCreatingAgent
                    ? tUi("createEmailAgent")
                    : tUi("editValue0", { value0: String(editorAgent?.name) })}
                </h1>
                {editorAgent && <AgentStatusBadge status={editorAgent.status} />}
              </div>
              <p key={isCreatingAgent
                  ? tUi("setUpTheBasicConfigurationForYourNew")
                  : editorAgent?.inboxes?.length
                    ? tUi("value0InboxValue1Connected", { count: editorAgent.inboxes.length })
                    : tUi("manageEmailAgentSettings")} className="mt-1 text-muted-foreground">
                {isCreatingAgent
                  ? tUi("setUpTheBasicConfigurationForYourNew")
                  : editorAgent?.inboxes?.length
                    ? tUi("value0InboxValue1Connected", { count: editorAgent.inboxes.length })
                    : tUi("manageEmailAgentSettings")}
              </p>
            </div>
          </div>

          {!isCreatingAgent && editorAgent && (
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center lg:shrink-0 lg:justify-end">
              <Button
                variant="outline"
                onClick={() => setTestChatAgent(editorAgent)}
                className="w-full sm:w-auto"
              >
                <Play className="w-4 h-4 mr-2" />
                <span><span key={tUi("test")}>{tUi("test")}</span></span>
              </Button>
              {canViewAnalytics && (
                <Button
                  variant="outline"
                  onClick={() => router.push(`?page=agent-analytics&agentId=${editorAgentId}`)}
                  className="w-full sm:w-auto"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  <span><span key={tUi("analytics")}>{tUi("analytics")}</span></span>
                </Button>
              )}
              {canViewEmails && (
                <Button
                  onClick={() => router.push(`?page=agent-emails&agentId=${editorAgentId}`)}
                  className={canViewAnalytics ? "col-span-2 w-full sm:col-auto sm:w-auto" : "w-full sm:w-auto"}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  <span><span key={tUi("conversations")}>{tUi("conversations")}</span></span>
                </Button>
              )}
            </div>
          )}
        </div>

        {statusMessage && (
          <Alert
            className={
              statusMessage.type === "success"
                ? "border-green-200 bg-green-50"
                : "border-red-200 bg-red-50"
            }
          >
            <AlertDescription
              className={
                statusMessage.type === "success"
                  ? "text-green-800"
                  : "text-red-800"
              }
            >
              {statusMessage.message}
            </AlertDescription>
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as SettingsTab)}
          className="space-y-4"
        >
          <div className="overflow-x-auto pb-2 [scrollbar-width:thin]">
          <TabsList className={`grid w-max ${canViewAnalytics ? "grid-cols-8" : "grid-cols-7"} sm:w-full`}>
            <TabsTrigger value="general"><span key={tUi("general")}>{tUi("general")}</span></TabsTrigger>
            <TabsTrigger value="inbox" disabled={isCreatingAgent}>
              <span key={tUi("inbox")}>{tUi("inbox")}</span></TabsTrigger>
            <TabsTrigger value="auto-reply" disabled={isCreatingAgent}>
              <span key={tUi("tools")}>{tUi("tools")}</span></TabsTrigger>
            <TabsTrigger value="kb" disabled={isCreatingAgent}>
              <span key={tUi("knowledge")}>{tUi("knowledge")}</span></TabsTrigger>
            <TabsTrigger value="crm" disabled={isCreatingAgent}>
              <span key={"CRM"}>{"CRM"}</span></TabsTrigger>
            <TabsTrigger value="delivery" disabled={isCreatingAgent}>
              <span key={tUi("delivery")}>{tUi("delivery")}</span></TabsTrigger>
            {canViewAnalytics && (
              <TabsTrigger value="analytics" disabled={isCreatingAgent}>
                <span key={tUi("analytics")}>{tUi("analytics")}</span></TabsTrigger>
            )}
            <TabsTrigger value="danger" disabled={isCreatingAgent}>
              <span key={tUi("danger")}>{tUi("danger")}</span></TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="inbox" className="space-y-4 pb-20">
            {editorAgentId && (
              <InboxSettings agentId={editorAgentId} onInboxesChange={refreshAgents} />
            )}
          </TabsContent>

          <TabsContent value="general" className="space-y-4 pb-20">
            <GeneralSettings
              agent={isCreatingAgent ? null : (editorAgent ?? null)}
              isCreating={isCreatingAgent}
              onSave={handleAgentSaved}
              onSavingChange={setIsSavingAgent}
            />
          </TabsContent>

          <TabsContent value="auto-reply" className="space-y-4 pb-20">
            {editorAgent && <AutoReplySettings agent={editorAgent} section="tools" />}
          </TabsContent>

          <TabsContent value="kb" className="space-y-4 pb-20">
            {editorAgentId && <KnowledgeBaseSettings agentId={editorAgentId} />}
          </TabsContent>

          <TabsContent value="crm" className="space-y-4 pb-20">
            {editorAgentId && <CrmSettings agentId={editorAgentId} />}
          </TabsContent>

          <TabsContent value="delivery" className="space-y-4 pb-20">
            {editorAgentId && (
              <AgentDeliverySettings
                agentId={editorAgentId}
                scopeType="email_agent"
              />
            )}
          </TabsContent>

          {canViewAnalytics && (
            <TabsContent value="analytics" className="space-y-4 pb-20">
              {editorAgentId && <AnalyticsSettings agentId={editorAgentId} />}
            </TabsContent>
          )}

          <TabsContent value="danger" className="space-y-4 pb-20">
            {editorAgentId && (
              <DangerZoneSettings
                agentId={editorAgentId}
                onDelete={() => {
                  refreshAgents();
                  handleBackToList();
                }}
              />
            )}
          </TabsContent>
        </Tabs>

        {editorAgent && testChatAgent && (
          <TestChatDialog
            open
            onOpenChange={open => {
              if (!open) setTestChatAgent(null)
            }}
            agentId={testChatAgent.id}
            agentName={testChatAgent.name}
            useAiTools={testChatAgent.config?.auto_reply?.use_ai_tools ?? false}
          />
        )}

        {/* Sticky Footer - only when creating new agent */}
        {isCreatingAgent && (
          <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background p-3 shadow-lg md:left-64 md:p-4">
            <div className="mx-auto flex max-w-5xl flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3 md:pr-48">
              <Button className="w-full sm:w-auto" variant="outline" onClick={handleBackToList} disabled={isSavingAgent}>
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button
                type="submit"
                form="agent-form"
                className="w-full bg-blue-600 hover:bg-blue-700 sm:w-auto"
                disabled={isSavingAgent}
              >
                {isSavingAgent ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span key={tUi("creating")}>{tUi("creating")}</span></>
                ) : (
                  <><TranslationText>{tUi.rich("tag0Tag0CreateAgent", { tag0: () => (<Save className="w-4 h-4 mr-2" />) })}</TranslationText></>
                )}
              </Button>
            </div>
          </div>
        )}
        </div>
      </TooltipProvider>
    );
  }

  // List view
  if (!canViewAgents) {
    return (
      <TooltipProvider>
        <div className="p-6 max-w-7xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></CardTitle>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>
                  <span key={tUi("youDonTHavePermissionToAccessEmail")}>{tUi("youDonTHavePermissionToAccessEmail")}</span></AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </TooltipProvider>
    );
  }

  if (isLoading) {
    return (
      <TooltipProvider>
        <div className="p-6 max-w-7xl mx-auto">
          <PageLoader message={tUi("loadingAgents")} />
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            <span key={tUi("emailAgents")}>{tUi("emailAgents")}</span></h1>
          <p className="text-muted-foreground mt-1">
            <span key={tUi("manageYourEmailAgentsAndInboxConfigurations")}>{tUi("manageYourEmailAgentsAndInboxConfigurations")}</span></p>
        </div>
        <div className="flex items-center gap-3">
          {canManageAgents && (
            <Button
              onClick={handleCreateAgent}
            >
              <Plus className="w-4 h-4 mr-2" />
              <span><span key={tUi("createAgent")}>{tUi("createAgent")}</span></span>
            </Button>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {statusMessage && (
        <Alert
          className={
            statusMessage.type === "success"
              ? "border-green-200 bg-green-50"
              : "border-red-200 bg-red-50"
          }
        >
          <AlertDescription
            className={
              statusMessage.type === "success"
                ? "text-green-800"
                : "text-red-800"
            }
          >
            {statusMessage.message}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={tUi("searchAgentsByNameOrInbox")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {filteredAgents.length} <span><span key={tUi("of")}>{tUi("of")}</span></span> {agents.length} <span><span key={tUi("agents")}>{tUi("agents")}</span></span>
          </div>
        </div>
      </div>

      {/* Agents Grid */}
      {filteredAgents.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 key={agents.length === 0 ? tUi("noEmailAgentsYet") : tUi("noAgentsFound")} className="text-lg font-medium text-foreground mb-2">
            {agents.length === 0 ? tUi("noEmailAgentsYet") : tUi("noAgentsFound")}
          </h3>
          <p key={agents.length === 0
              ? tUi("createYourFirstEmailAgentToStartProcessing")
              : tUi("tryAdjustingYourSearchTerms")} className="text-gray-600 mb-6">
            {agents.length === 0
              ? tUi("createYourFirstEmailAgentToStartProcessing")
              : tUi("tryAdjustingYourSearchTerms")}
          </p>
          {canManageAgents && agents.length === 0 && (
            <Button
              onClick={handleCreateAgent}
            >
              <Plus className="w-4 h-4 mr-2" />
              <span><span key={tUi("createYourFirstAgent")}>{tUi("createYourFirstAgent")}</span></span>
            </Button>
          )}
        </div>
      ) : (
        <AgentsGrid>
          {filteredAgents.map((agent) => (
            <EmailAgentCard
              key={agent.id}
              agent={agent}
              onEdit={handleEditAgent}
              onTest={setTestChatAgent}
              canManage={canManageAgents}
              canViewEmails={canViewEmails}
              canViewAnalytics={canViewAnalytics}
              pendingFeedbackCount={feedbackCounts[agent.id]}
              integrationStatus={integrationStatusesQuery.data?.[agent.id]}
            />
          ))}
        </AgentsGrid>
      )}

      {testChatAgent && (
        <TestChatDialog
          open
          onOpenChange={open => {
            if (!open) setTestChatAgent(null)
          }}
          agentId={testChatAgent.id}
          agentName={testChatAgent.name}
          useAiTools={testChatAgent.config?.auto_reply?.use_ai_tools ?? false}
        />
      )}
      </div>
    </TooltipProvider>
  );
}
